import { useCallback, useEffect, useRef, useState } from 'react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
    $getSelection,
    $setSelection,
    $isRangeSelection,
    $createParagraphNode,
    FORMAT_TEXT_COMMAND,
    FORMAT_ELEMENT_COMMAND,
    UNDO_COMMAND,
    REDO_COMMAND,
    CAN_UNDO_COMMAND,
    CAN_REDO_COMMAND,
    COMMAND_PRIORITY_CRITICAL
} from 'lexical'
import {
    $createHeadingNode,
    $isHeadingNode
} from '@lexical/rich-text'
import {
    INSERT_ORDERED_LIST_COMMAND,
    INSERT_UNORDERED_LIST_COMMAND,
    REMOVE_LIST_COMMAND,
    $isListNode,
    ListNode
} from '@lexical/list'
import { $isLinkNode, $toggleLink, TOGGLE_LINK_COMMAND } from '@lexical/link'
import { $getNearestNodeOfType, mergeRegister } from '@lexical/utils'
import { $setBlocksType, $patchStyleText, $getSelectionStyleValueForProperty } from '@lexical/selection'
import {
    BiBold,
    BiItalic,
    BiUnderline,
    BiAlignLeft,
    BiAlignMiddle,
    BiAlignRight,
    BiAlignJustify,
    BiListUl,
    BiListOl,
    BiLink,
    BiUndo,
    BiRedo,
    BiText
} from 'react-icons/bi'
import {
    ToolbarContainer, ToolbarGroup, ToolbarButton, StyleSelect,
    StyleDropdownWrapper, StyleDropdownMenu, StyleDropdownItem
} from './FormattingStyles'
import TextColorPicker from './TextColorPicker'

const ALIGN_OPTIONS = [
    { value: 'left', label: 'Left', icon: BiAlignLeft },
    { value: 'center', label: 'Center', icon: BiAlignMiddle },
    { value: 'right', label: 'Right', icon: BiAlignRight },
    { value: 'justify', label: 'Justify', icon: BiAlignJustify },
]

const TEXT_STYLE_OPTIONS = [
    { value: 'paragraph', label: 'Normal' },
    { value: 'h1', label: 'Heading 1' },
    { value: 'h2', label: 'Heading 2' },
    { value: 'h3', label: 'Heading 3' },
    { value: 'large', label: 'Large' },
    { value: 'small', label: 'Small' }
]

const isMobile = () => window.innerWidth < 640

export default function RichTextToolbar({ showUndoRedo, openLinkModal }) {
    const [editor] = useLexicalComposerContext()
    const [isBold, setIsBold] = useState(false)
    const [isItalic, setIsItalic] = useState(false)
    const [isUnderline, setIsUnderline] = useState(false)
    const [blockType, setBlockType] = useState('paragraph')
    const [isLink, setIsLink] = useState(false)
    const [canUndo, setCanUndo] = useState(false)
    const [canRedo, setCanRedo] = useState(false)
    const [mobile, setMobile] = useState(isMobile())
    const [styleOpen, setStyleOpen] = useState(false)
    const [listOpen, setListOpen] = useState(false)
    const [alignOpen, setAlignOpen] = useState(false)
    const [currentAlign, setCurrentAlign] = useState('left')
    const [colorOpen, setColorOpen] = useState(false)
    const [currentColor, setCurrentColor] = useState('#000000')
    const styleDropdownRef = useRef(null)
    const listDropdownRef = useRef(null)
    const alignDropdownRef = useRef(null)
    const colorDropdownRef = useRef(null)
    const lastSelectionRef = useRef(null)

    useEffect(() => {
        const handler = () => setMobile(isMobile())
        window.addEventListener('resize', handler)
        return () => window.removeEventListener('resize', handler)
    }, [])

    // Close dropdowns on outside click
    useEffect(() => {
        if (!styleOpen) return
        const handler = (e) => {
            if (styleDropdownRef.current && !styleDropdownRef.current.contains(e.target)) {
                setStyleOpen(false)
            }
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [styleOpen])

    useEffect(() => {
        if (!listOpen) return
        const handler = (e) => {
            if (listDropdownRef.current && !listDropdownRef.current.contains(e.target)) {
                setListOpen(false)
            }
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [listOpen])

    useEffect(() => {
        if (!alignOpen) return
        const handler = (e) => {
            if (alignDropdownRef.current && !alignDropdownRef.current.contains(e.target)) {
                setAlignOpen(false)
            }
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [alignOpen])

    useEffect(() => {
        if (!colorOpen) return
        const handler = (e) => {
            if (colorDropdownRef.current && !colorDropdownRef.current.contains(e.target)) {
                setColorOpen(false)
            }
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [colorOpen])


    const updateToolbar = useCallback(() => {
        const selection = $getSelection()
        if ($isRangeSelection(selection)) {
            lastSelectionRef.current = selection.clone()
            setIsBold(selection.hasFormat('bold'))
            setIsItalic(selection.hasFormat('italic'))
            setIsUnderline(selection.hasFormat('underline'))

            const anchorNode = selection.anchor.getNode()
            const element = anchorNode.getKey() === 'root'
                ? anchorNode
                : anchorNode.getTopLevelElementOrThrow()

            const elementKey = element.getKey()
            const elementDOM = editor.getElementByKey(elementKey)

            if (elementDOM) {
                if ($isListNode(element)) {
                    const parentList = $getNearestNodeOfType(anchorNode, ListNode)
                    const type = parentList ? parentList.getListType() : element.getListType()
                    setBlockType(type === 'bullet' ? 'ul' : 'ol')
                } else {
                    const type = $isHeadingNode(element) ? element.getTag() : element.getType()
                    // map 'paragraph' → 'paragraph', 'heading' tag → 'h1'/'h2'/'h3'
                    setBlockType(type)
                }
            }

            const node = selection.anchor.getNode()
            const parent = node.getParent()
            setIsLink($isLinkNode(parent) || $isLinkNode(node))

            const formatType = element.getFormatType ? element.getFormatType() : ''
            setCurrentAlign(formatType || 'left')

            const color = $getSelectionStyleValueForProperty(selection, 'color', '#000000')
            setCurrentColor(color || '#000000')
        }
    }, [editor])

    useEffect(() => {
        return mergeRegister(
            editor.registerUpdateListener(({ editorState }) => {
                editorState.read(() => {
                    updateToolbar()
                })
            }),
            editor.registerCommand(CAN_UNDO_COMMAND, (payload) => {
                setCanUndo(payload)
                return false
            }, COMMAND_PRIORITY_CRITICAL),
            editor.registerCommand(CAN_REDO_COMMAND, (payload) => {
                setCanRedo(payload)
                return false
            }, COMMAND_PRIORITY_CRITICAL)
        )
    }, [editor, updateToolbar])

    const formatHeading = (tag) => {
        editor.update(() => {
            const selection = $getSelection()
            if ($isRangeSelection(selection)) {
                if (blockType === tag) {
                    $setBlocksType(selection, () => $createParagraphNode())
                } else {
                    $setBlocksType(selection, () => $createHeadingNode(tag))
                }
            }
        })
    }

    const formatParagraph = () => {
        if (blockType !== 'paragraph') {
            editor.update(() => {
                const selection = $getSelection()
                if ($isRangeSelection(selection)) {
                    $setBlocksType(selection, () => $createParagraphNode())
                }
            })
        }
    }

    const formatList = (listType) => {
        if (listType === 'ul') {
            if (blockType === 'ul') {
                editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
            } else {
                editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
            }
        } else {
            if (blockType === 'ol') {
                editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
            } else {
                editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
            }
        }
    }

    const insertLink = () => {
        if (isLink) {
            editor.dispatchCommand(TOGGLE_LINK_COMMAND, null)
        } else {
            const savedSelection = lastSelectionRef.current
            openLinkModal('', (url) => {
                editor.update(() => {
                    if (savedSelection) $setSelection(savedSelection)
                    $toggleLink(url)
                })
            })
        }
    }

    const handleStyleChange = (e) => {
        const val = e.target.value
        switch (val) {
            case 'paragraph': formatParagraph(); break
            case 'h1': formatHeading('h1'); break
            case 'h2': formatHeading('h2'); break
            case 'h3': formatHeading('h3'); break
            case 'large':
                editor.update(() => {
                    const selection = $getSelection()
                    if ($isRangeSelection(selection)) {
                        $setBlocksType(selection, () => $createParagraphNode())
                        $patchStyleText(selection, { 'font-size': '1.2em' })
                    }
                })
                break
            case 'small':
                editor.update(() => {
                    const selection = $getSelection()
                    if ($isRangeSelection(selection)) {
                        $setBlocksType(selection, () => $createParagraphNode())
                        $patchStyleText(selection, { 'font-size': '0.8em' })
                    }
                })
                break
            default: break
        }
    }

    const applyColor = (color) => {
        editor.update(() => {
            const selection = $getSelection()
            if ($isRangeSelection(selection)) {
                $patchStyleText(selection, { color })
            }
        })
        setCurrentColor(color)
        setColorOpen(false)
    }

    const applyStyle = (val) => {
        handleStyleChange({ target: { value: val } })
        setStyleOpen(false)
    }

    // map blockType to select value
    const selectValue = ['h1', 'h2', 'h3'].includes(blockType) ? blockType
        : blockType === 'ul' || blockType === 'ol' ? 'paragraph'
            : 'paragraph'

    if (mobile) {
        return (
            <ToolbarContainer>
                <ToolbarButton onMouseDown={(e) => { e.preventDefault(); editor.dispatchCommand(UNDO_COMMAND, undefined) }} disabled={!canUndo} title="Undo">
                    <BiUndo />
                </ToolbarButton>
                <StyleDropdownWrapper ref={styleDropdownRef}>
                    <ToolbarButton
                        $active={styleOpen}
                        onMouseDown={(e) => { e.preventDefault(); setStyleOpen(o => !o) }}
                        title="Text style"
                    >
                        <BiText />
                    </ToolbarButton>
                    {styleOpen && (
                        <StyleDropdownMenu>
                            {TEXT_STYLE_OPTIONS.map(o => (
                                <StyleDropdownItem
                                    key={o.value}
                                    $active={selectValue === o.value}
                                    onMouseDown={(e) => { e.preventDefault(); applyStyle(o.value) }}
                                >
                                    {o.label}
                                </StyleDropdownItem>
                            ))}
                        </StyleDropdownMenu>
                    )}
                </StyleDropdownWrapper>
                <ToolbarButton $active={isBold} onMouseDown={(e) => { e.preventDefault(); editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold') }} title="Bold">
                    <BiBold />
                </ToolbarButton>
                <ToolbarButton $active={isItalic} onMouseDown={(e) => { e.preventDefault(); editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic') }} title="Italic">
                    <BiItalic />
                </ToolbarButton>
                {(() => {
                    const ActiveIcon = ALIGN_OPTIONS.find(a => a.value === currentAlign)?.icon || BiAlignLeft
                    return (
                        <StyleDropdownWrapper ref={alignDropdownRef}>
                            <ToolbarButton
                                $active={alignOpen}
                                onMouseDown={(e) => { e.preventDefault(); setAlignOpen(o => !o) }}
                                title="Alignment"
                            >
                                <ActiveIcon />
                            </ToolbarButton>
                            {alignOpen && (
                                <StyleDropdownMenu>
                                    {ALIGN_OPTIONS.map(({ value, label, icon: Icon }) => (
                                        <StyleDropdownItem
                                            key={value}
                                            $active={currentAlign === value}
                                            onMouseDown={(e) => {
                                                e.preventDefault()
                                                editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, value)
                                                setCurrentAlign(value)
                                                setAlignOpen(false)
                                            }}
                                        >
                                            <Icon style={{ marginRight: 6, verticalAlign: 'middle' }} />
                                            {label}
                                        </StyleDropdownItem>
                                    ))}
                                </StyleDropdownMenu>
                            )}
                        </StyleDropdownWrapper>
                    )
                })()}
                <StyleDropdownWrapper ref={listDropdownRef}>
                    <ToolbarButton
                        $active={listOpen || blockType === 'ul' || blockType === 'ol'}
                        onMouseDown={(e) => { e.preventDefault(); setListOpen(o => !o) }}
                        title="List"
                    >
                        {blockType === 'ol' ? <BiListOl /> : <BiListUl />}
                    </ToolbarButton>
                    {listOpen && (
                        <StyleDropdownMenu>
                            <StyleDropdownItem
                                $active={blockType === 'ul'}
                                onMouseDown={(e) => { e.preventDefault(); formatList('ul'); setListOpen(false) }}
                            >
                                <BiListUl style={{ marginRight: 6, verticalAlign: 'middle' }} />
                                Bullet List
                            </StyleDropdownItem>
                            <StyleDropdownItem
                                $active={blockType === 'ol'}
                                onMouseDown={(e) => { e.preventDefault(); formatList('ol'); setListOpen(false) }}
                            >
                                <BiListOl style={{ marginRight: 6, verticalAlign: 'middle' }} />
                                Numbered List
                            </StyleDropdownItem>
                        </StyleDropdownMenu>
                    )}
                </StyleDropdownWrapper>
                <TextColorPicker
                    isOpen={colorOpen}
                    onToggle={() => setColorOpen(o => !o)}
                    currentColor={currentColor}
                    onColorChange={applyColor}
                    dropdownRef={colorDropdownRef}
                    mobile={true}
                />
                <ToolbarButton $active={isLink} onMouseDown={(e) => { e.preventDefault(); insertLink() }} title="Link">
                    <BiLink />
                </ToolbarButton>
            </ToolbarContainer>
        )
    }

    return (
        <ToolbarContainer>
            {showUndoRedo && (
                <ToolbarGroup>
                    <ToolbarButton onMouseDown={(e) => { e.preventDefault(); editor.dispatchCommand(UNDO_COMMAND, undefined) }} disabled={!canUndo} title="Undo">
                        <BiUndo />
                    </ToolbarButton>
                    <ToolbarButton onMouseDown={(e) => { e.preventDefault(); editor.dispatchCommand(REDO_COMMAND, undefined) }} disabled={!canRedo} title="Redo">
                        <BiRedo />
                    </ToolbarButton>
                </ToolbarGroup>
            )}

            <ToolbarGroup>
                <StyleSelect value={selectValue} onChange={handleStyleChange} title="Text style">
                    {TEXT_STYLE_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </StyleSelect>
            </ToolbarGroup>

            <ToolbarGroup>
                <ToolbarButton $active={isBold} onMouseDown={(e) => { e.preventDefault(); editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold') }} title="Bold">
                    <BiBold />
                </ToolbarButton>
                <ToolbarButton $active={isItalic} onMouseDown={(e) => { e.preventDefault(); editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic') }} title="Italic">
                    <BiItalic />
                </ToolbarButton>
                <ToolbarButton $active={isUnderline} onMouseDown={(e) => { e.preventDefault(); editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline') }} title="Underline">
                    <BiUnderline />
                </ToolbarButton>
            </ToolbarGroup>

            <ToolbarGroup>
                {(() => {
                    const ActiveIcon = ALIGN_OPTIONS.find(a => a.value === currentAlign)?.icon || BiAlignLeft
                    return (
                        <StyleDropdownWrapper ref={alignDropdownRef}>
                            <ToolbarButton
                                $active={alignOpen}
                                onMouseDown={(e) => { e.preventDefault(); setAlignOpen(o => !o) }}
                                title="Alignment"
                            >
                                <ActiveIcon />
                            </ToolbarButton>
                            {alignOpen && (
                                <StyleDropdownMenu>
                                    {ALIGN_OPTIONS.map(({ value, label, icon: Icon }) => (
                                        <StyleDropdownItem
                                            key={value}
                                            $active={currentAlign === value}
                                            onMouseDown={(e) => {
                                                e.preventDefault()
                                                editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, value)
                                                setCurrentAlign(value)
                                                setAlignOpen(false)
                                            }}
                                        >
                                            <Icon style={{ marginRight: 6, verticalAlign: 'middle' }} />
                                            {label}
                                        </StyleDropdownItem>
                                    ))}
                                </StyleDropdownMenu>
                            )}
                        </StyleDropdownWrapper>
                    )
                })()}
            </ToolbarGroup>

            <ToolbarGroup>
                <StyleDropdownWrapper ref={listDropdownRef}>
                    <ToolbarButton
                        $active={listOpen || blockType === 'ul' || blockType === 'ol'}
                        onMouseDown={(e) => { e.preventDefault(); setListOpen(o => !o) }}
                        title="List"
                    >
                        {blockType === 'ol' ? <BiListOl /> : <BiListUl />}
                    </ToolbarButton>
                    {listOpen && (
                        <StyleDropdownMenu>
                            <StyleDropdownItem
                                $active={blockType === 'ul'}
                                onMouseDown={(e) => { e.preventDefault(); formatList('ul'); setListOpen(false) }}
                            >
                                <BiListUl style={{ marginRight: 6, verticalAlign: 'middle' }} />
                                Bullet List
                            </StyleDropdownItem>
                            <StyleDropdownItem
                                $active={blockType === 'ol'}
                                onMouseDown={(e) => { e.preventDefault(); formatList('ol'); setListOpen(false) }}
                            >
                                <BiListOl style={{ marginRight: 6, verticalAlign: 'middle' }} />
                                Numbered List
                            </StyleDropdownItem>
                        </StyleDropdownMenu>
                    )}
                </StyleDropdownWrapper>
            </ToolbarGroup>

            <ToolbarGroup>
                <TextColorPicker
                    isOpen={colorOpen}
                    onToggle={() => setColorOpen(o => !o)}
                    currentColor={currentColor}
                    onColorChange={applyColor}
                    dropdownRef={colorDropdownRef}
                />
            </ToolbarGroup>

            <ToolbarGroup>
                <ToolbarButton $active={isLink} onMouseDown={(e) => { e.preventDefault(); insertLink() }} title="Link">
                    <BiLink />
                </ToolbarButton>
            </ToolbarGroup>
        </ToolbarContainer>
    )
}
